import { DEFAULT_PROJECTS_LIMIT, MasterCategoryType } from '@shared/constants';

// Configs
import ENV from '@shared/configs';

export const BASE_URL_ENDPOINT = '/api/v1';
export const MASTER_DATA_BASE_URL = '/api/v1/configs?types=';

export const END_POINTS = {
  GET_USERS: (limit?: number, cursor?: string, query?: string) => {
    const queryString = new URLSearchParams();

    if (limit) queryString.set('limit', String(limit));
    if (cursor) queryString.set('cursor', cursor);
    if (query) queryString.set('query', query);

    return `${BASE_URL_ENDPOINT}/users?${queryString.toString()}`;
  },

  GET_CUSTOM_TOKEN: `${BASE_URL_ENDPOINT}/auth/custom-token`,

  GET_RESUME: (templateId: string) =>
    `${BASE_URL_ENDPOINT}/resumes/${templateId}`,
  GET_USER_RESUME_BY_ID: (resumeId: string) =>
    `${BASE_URL_ENDPOINT}/resumes/${resumeId}`,

  RESUMES: `${BASE_URL_ENDPOINT}/resumes`,
  EXPERIENCE: (resumeId: string) =>
    `${BASE_URL_ENDPOINT}/resumes/${resumeId}/journeys`,

  UPDATE_TEMPLATE_ID: (resumeId: string) =>
    `${BASE_URL_ENDPOINT}/resumes/${resumeId}`,

  GET_USERS_RESUMES: (userId: string) =>
    `${BASE_URL_ENDPOINT}/users/${userId}/resumes`,
  PERSONAL_INFORMATION: (resumeId: string) =>
    `${BASE_URL_ENDPOINT}/resumes/${resumeId}/info`,
  PERSONAL_INFORMATION_WITH_AVATAR: (resumeId: string) =>
    `${BASE_URL_ENDPOINT}/resumes/${resumeId}/info/avatar`,
  CERTIFICATION: (resumeId: string) =>
    `${BASE_URL_ENDPOINT}/resumes/${resumeId}/certs`,
  TECHNICAL_SKILLS: (resumeId: string) =>
    `${BASE_URL_ENDPOINT}/resumes/${resumeId}/skills`,
  EDUCATION: (resumeId: string) =>
    `${BASE_URL_ENDPOINT}/resumes/${resumeId}/education`,
  SOCIAL_LINKS: (resumeId: string) =>
    `${BASE_URL_ENDPOINT}/resumes/${resumeId}/links`,
  GET_USER_PROFILE: (username = 'profile') =>
    `${BASE_URL_ENDPOINT}/users/${username}`,
  GET_USER_SIMPLE_PROFILE: (identity: string) =>
    `${BASE_URL_ENDPOINT}/users/${identity}?simple=true`,
  GET_STATISTICS: `${BASE_URL_ENDPOINT}/stats`,
  DELETE_RESUME: (resumeId: string) =>
    `${BASE_URL_ENDPOINT}/resumes/${resumeId}`,

  // Employee
  GET_EMPLOYEE_INFO: (identity: string) =>
    `${BASE_URL_ENDPOINT}/employees/${identity}`,
  GET_EMPLOYEE_PROFILE: (identity: string) =>
    `${BASE_URL_ENDPOINT}/employees/${identity}/profile`,
  GET_EMPLOYEE_PROJECT: (
    identity: string,
    limit = DEFAULT_PROJECTS_LIMIT,
    cursor?: string,
  ) => {
    const queryString = new URLSearchParams();

    if (limit) queryString.set('limit', String(limit));
    if (cursor) queryString.set('cursor', encodeURIComponent(cursor));

    return `${BASE_URL_ENDPOINT}/employees/${identity}/projects?${queryString.toString()}`;
  },
  GET_EMPLOYEE_ACTIVITIES: (identity: string, cursor?: string) =>
    `${BASE_URL_ENDPOINT}/employees/${identity}/activities${
      cursor ? `?cursor=${cursor}` : ''
    }`,
  GET_EMPLOYEE_TRAININGS: (
    identity: string,
    limit?: number,
    cursor?: string,
    isSimple = false,
  ) => {
    const params = new URLSearchParams({ simple: String(isSimple) });
    if (limit) params.append('limit', String(limit));
    if (cursor) params.append('cursor', encodeURIComponent(cursor));

    return `${BASE_URL_ENDPOINT}/employees/${identity}/trainings?${params.toString()}`;
  },
  GET_EMPLOYEE_LUNCH_REGISTRATION: (identity: string) =>
    `${BASE_URL_ENDPOINT}/employees/${identity}/lunch-registrations`,
  GET_EMPLOYEE_TIME_OFF_REQUESTS: (identity: string) =>
    `${BASE_URL_ENDPOINT}/employees/${identity}/time-offs`,
  GET_EMPLOYEE_RELATIONSHIP: (identity: string) =>
    `${BASE_URL_ENDPOINT}/employees/${identity}/relationships`,
  GET_SIMPLE_EMPLOYEE_RELATIONSHIP: (
    identity: string,
    employeeIdentity: string,
  ) =>
    `${BASE_URL_ENDPOINT}/employees/${identity}/relationship-with?employeeIdentity=${employeeIdentity}`,
  YEAR_IN_REVIEW_VIDEO: (identity: string, year: string) =>
    `${BASE_URL_ENDPOINT}/employees/${identity}/year-reviews/stream?year=${year}`,

  // Auth
  SIGN_IN_SESSION: (clerkJWT: string) =>
    `/v1/client/sign_ins?__clerk_db_jwt=${clerkJWT}`,

  // Combined master data
  GET_MASTER_DATA: (types: MasterCategoryType[]) =>
    `${MASTER_DATA_BASE_URL}${types.join(',')}`,

  // Mapbox API
  MAPBOX_FORWARD_GEOCODING: (
    query: string,
    accessToken: string,
    language = ENV.MAPBOX_LANGUAGE,
    limit = ENV.MAPBOX_LIMIT,
  ) =>
    `${ENV.MAPBOX_BASE_URL}${ENV.MAPBOX_ENDPOINTS.FORWARD_GEOCODING}?q=${encodeURIComponent(query)}&access_token=${accessToken}&language=${language}&limit=${limit}`,

  // Search form modal
  SEARCH_FORM_MODAL: (searchQuery: string) =>
    `${BASE_URL_ENDPOINT}/actions?query=${searchQuery}`,
  SEARCH_HANDLE_ACTION: `${BASE_URL_ENDPOINT}/actions`,

  // Project
  GET_PROJECT_INFO: (projectIdentity: string) =>
    `${BASE_URL_ENDPOINT}/projects/${encodeURIComponent(projectIdentity)}`,
  GET_PROJECT_SIMPLE_INFO: (identity: string) =>
    `${BASE_URL_ENDPOINT}/projects/${encodeURIComponent(identity)}?simple=true`,
  GET_PROJECT_MEMBER: (projectId: string) =>
    `${BASE_URL_ENDPOINT}/projects/${encodeURIComponent(projectId)}/members`,
  GET_ALL_PROJECTS: (limit?: number, cursor?: string) => {
    const queryString = new URLSearchParams();

    if (limit) queryString.set('limit', String(limit));
    if (cursor) queryString.set('cursor', encodeURIComponent(cursor));

    return `${BASE_URL_ENDPOINT}/projects?${queryString.toString()}`;
  },
  GET_PROJECT_ACTIVITIES: (id: string, cursor?: string) =>
    `${BASE_URL_ENDPOINT}/projects/${id}/activities${
      cursor ? `?cursor=${cursor}` : ''
    }`,

  // ChatBot
  CHAT_ACTIONS: `${BASE_URL_ENDPOINT}/actions/chat`,
  STOP_CHAT_ACTIONS: `${process.env.NEXT_PUBLIC_AGENT_ENDPOINT}/api/copilotkit`,
  CHAT_ACTIONS_TRAININGS: `${BASE_URL_ENDPOINT}/actions/chat/trainings`,

  GET_SIMPLE_USERS: (limit?: number, cursor?: string, query?: string) => {
    const queryString = new URLSearchParams();

    if (limit) queryString.set('limit', String(limit));
    if (cursor) queryString.set('cursor', cursor);
    if (query) queryString.set('query', query);

    return `${BASE_URL_ENDPOINT}/employees?${queryString.toString()}&simple=true`;
  },
} as const;
